<script setup>
import { computed, ref } from 'vue';
import { Letter } from 'vue-letter';
import { allowedCssProperties } from 'lettersanitizer';
import { useI18n } from 'vue-i18n';

import Icon from 'next/icon/Icon.vue';
import { sanitizeHtmlContent } from './sanitizeHtmlContent.js';
import { invertEmailColors } from './invertColors.js';
import { ensureReadableText } from './ensureReadableText.js';
import { EmailQuoteExtractor } from './removeReply.js';
import { plainTextToHtml } from './plainTextToHtml.js';
import { useIsDarkThemeActive } from 'dashboard/helper/themeHelper';
import BaseBubble from 'next/message/bubbles/Base.vue';
import FormattedContent from 'next/message/bubbles/Text/FormattedContent.vue';
import AttachmentChips from 'next/message/chips/AttachmentChips.vue';
import EmailMeta from './EmailMeta.vue';
import TranslationToggle from 'dashboard/components-next/message/TranslationToggle.vue';

import { useMessageContext } from '../../provider.js';
import { MESSAGE_TYPES } from '../../constants.js';
import { useTranslations } from 'dashboard/composables/useTranslations';
import { useMessageTranslation } from 'dashboard/composables/useMessageTranslation';

const {
  content,
  contentAttributes,
  attachments,
  messageType,
  isEmailInbox,
  isPrivate,
  isFirstMessage,
  isAutomated,
} = useMessageContext();

const { hasTranslations, translationContent } =
  useTranslations(contentAttributes);

const { currentTranslationMode } = useMessageTranslation();

const { t } = useI18n();

const showQuotedMessage = ref(false);
const renderOriginal = ref(currentTranslationMode.value === 'original');
const showingOriginal = ref(currentTranslationMode.value === 'original');
const isShowingTranslation = computed(
  () => hasTranslations.value && !renderOriginal.value
);

const isOutgoing = computed(() => messageType.value === MESSAGE_TYPES.OUTGOING);
const isIncoming = computed(() => !isOutgoing.value);

const originalEmailText = computed(() => {
  const text =
    contentAttributes?.value?.email?.textContent?.full ?? content.value;
  return plainTextToHtml(text);
});

const originalEmailHtml = computed(
  () =>
    contentAttributes?.value?.email?.htmlContent?.full ??
    originalEmailText.value
);

const messageContent = computed(() => {
  if (isAutomated.value && !showQuotedMessage.value) {
    return `**[${t('CHAT_LIST.AUTOMATED_MESSAGE')}]**`;
  }

  if (hasTranslations.value && !renderOriginal.value) {
    return translationContent.value;
  }

  return content.value;
});

const textToShow = computed(() => {
  if (hasTranslations.value && !renderOriginal.value) {
    return translationContent.value;
  }
  return originalEmailText.value;
});

const isDarkThemeActive = useIsDarkThemeActive();

// Senders design for a white background, so in dark mode we mirror the colors
// they declare. Without it their white `bgcolor` keeps our light-on-dark text
// color and the message is unreadable. In either theme, text the sender left
// uncolored on a background they did color (a Gmail written in dark mode paints
// every paragraph near-black) then gets black or white so it stays readable.
const prepareHtml = html => {
  const sanitized = sanitizeHtmlContent(html);
  const themed = isDarkThemeActive.value
    ? invertEmailColors(sanitized)
    : sanitized;
  return ensureReadableText(themed, { isDark: isDarkThemeActive.value });
};

const fullHTML = computed(() => {
  if (hasTranslations.value && !renderOriginal.value) {
    return prepareHtml(translationContent.value);
  }

  return prepareHtml(originalEmailHtml.value);
});

const unquotedHTML = computed(() =>
  EmailQuoteExtractor.extractQuotes(fullHTML.value)
);

const hasQuotedMessage = computed(
  () => isAutomated.value || unquotedHTML.value !== null
);

// Ensure unique keys for <Letter> when toggling between original and translated views.
// This forces Vue to re-render the component and update content correctly.
const translationKeySuffix = computed(() => {
  if (renderOriginal.value) return 'original';
  if (hasTranslations.value) return 'translated';
  return 'original';
});

// Switching the color scheme rewrites the HTML the same way toggling the
// translation does, so it has to move the key as well.
const letterKey = computed(
  () =>
    `${translationKeySuffix.value}-${isDarkThemeActive.value ? 'dark' : 'light'}`
);

const handleSeeOriginal = () => {
  renderOriginal.value = !renderOriginal.value;
  showingOriginal.value = !showingOriginal.value;
};

const useFormattedContent = computed(() => {
  return (
    isOutgoing.value &&
    content.value &&
    (!isEmailInbox.value || !contentAttributes.value?.email?.htmlContent?.full)
  );
});
</script>

<template>
  <BaseBubble
    class="relative w-full"
    :class="{
      'bg-n-slate-4': isIncoming && !isPrivate,
      'bg-n-solid-blue': isOutgoing && !isPrivate && !isAutomated,
      'bg-n-teal-3': isOutgoing && !isPrivate && isAutomated,
      'bg-n-amber-3': isPrivate && isFirstMessage,
      '!bg-n-slate-3': isShowingTranslation && isIncoming,
      '!bg-n-blue-3': isShowingTranslation && isOutgoing,
    }"
    data-bubble-name="email"
  >
    <EmailMeta
      class="px-3 py-2"
      :class="{
        'border-b border-n-strong': isIncoming && !isPrivate,
        'border-b border-n-slate-8/20':
          isOutgoing && !isPrivate && !isAutomated,
        'border-b border-n-teal-8/20': isOutgoing && !isPrivate && isAutomated,
        'border-b border-n-amber-6': isPrivate && isFirstMessage,
      }"
    />
    <section
      :class="{
        'px-3': isIncoming,
        'px-3 py-2': isOutgoing,
      }"
    >
      <div class="overflow-y-auto relative">
        <FormattedContent
          v-if="useFormattedContent"
          class="text-n-slate-12 img-max-h-250"
          :content="messageContent"
        />
        <template v-else>
          <Letter
            v-if="!hasQuotedMessage || showQuotedMessage"
            :key="`letter-quoted-${letterKey}`"
            class-name="prose prose-bubble !max-w-none letter-render"
            :allowed-css-properties="[
              ...allowedCssProperties,
              'transform',
              'transform-origin',
              'display',
              'flex-direction',
              'justify-content',
              'align-items',
              'gap',
              'mso-table-lspace',
              'mso-table-rspace',
              'mso-line-height-rule',
              'mso-text-size-adjust',
              '-ms-text-size-adjust',
              '-webkit-text-size-adjust',
              '-ms-interpolation-mode',
            ]"
            :html="fullHTML"
            :text="textToShow"
            :allowed-schemas="['http', 'https', 'mailto', 'data']"
          />
          <FormattedContent
            v-else-if="isAutomated"
            class="text-n-slate-12"
            :content="messageContent"
          />
          <Letter
            v-else
            :key="`letter-unquoted-${letterKey}`"
            class-name="prose prose-bubble !max-w-none letter-render"
            :html="unquotedHTML"
            :allowed-css-properties="[
              ...allowedCssProperties,
              'transform',
              'transform-origin',
              'display',
              'flex-direction',
              'justify-content',
              'align-items',
              'gap',
              'mso-table-lspace',
              'mso-table-rspace',
              'mso-line-height-rule',
              'mso-text-size-adjust',
              '-ms-text-size-adjust',
              '-webkit-text-size-adjust',
              '-ms-interpolation-mode',
            ]"
            :text="textToShow"
            :allowed-schemas="['http', 'https', 'mailto', 'data']"
          />
        </template>
        <button
          v-if="hasQuotedMessage"
          class="text-n-slate-11 px-1 leading-none text-sm bg-n-alpha-black2 text-center flex items-center gap-1 mt-1 mb-1"
          @click="showQuotedMessage = !showQuotedMessage"
        >
          <template v-if="showQuotedMessage">
            {{
              isAutomated
                ? $t('CHAT_LIST.HIDE_AUTOMATED_MESSAGE')
                : $t('CHAT_LIST.HIDE_QUOTED_TEXT')
            }}
          </template>
          <template v-else>
            {{
              isAutomated
                ? $t('CHAT_LIST.SHOW_AUTOMATED_MESSAGE')
                : $t('CHAT_LIST.SHOW_QUOTED_TEXT')
            }}
          </template>
          <Icon
            :icon="
              showQuotedMessage
                ? 'i-lucide-chevron-up'
                : 'i-lucide-chevron-down'
            "
          />
        </button>
      </div>
    </section>
    <TranslationToggle
      v-if="hasTranslations"
      class="py-2 px-3"
      :showing-original="showingOriginal"
      @toggle="handleSeeOriginal"
    />
    <section
      v-if="
        Array.isArray(attachments) &&
        attachments.length &&
        (!isAutomated || showQuotedMessage)
      "
      class="px-4 pb-4 space-y-2"
    >
      <AttachmentChips :attachments="attachments" class="gap-1" />
    </section>
  </BaseBubble>
</template>

<style lang="scss">
// Tailwind resets break the rendering of google drive link in Gmail messages
// This fixes it using https://developer.mozilla.org/en-US/docs/Web/CSS/Attribute_selectors

.letter-render [class*='gmail_drive_chip'] {
  box-sizing: initial;
  @apply bg-n-slate-4 border-n-slate-6 rounded-md !important;

  a {
    @apply text-n-slate-12 !important;

    img {
      display: inline-block;
    }
  }
}
.letter-render {
  *,
  table,
  td,
  th,
  tr,
  tbody,
  thead,
  tfoot {
    box-sizing: border-box;
  }

  table {
    border-collapse: collapse;

    max-width: 100%;
  }

  table[align='center'] {
    margin-left: auto;
    margin-right: auto;
  }

  td:empty,
  th:empty {
    line-height: 0;

    &[height] {
      display: table-cell;
    }
  }

  td[style*='height'],
  th[style*='height'],
  tr[style*='height'] {
    min-height: 0;
  }
}

.prose-bubble {
  p {
    margin-top: 0px;

    &:empty {
      &::before {
        content: '\00A0';
        visibility: hidden;
      }
      margin-bottom: 0px;
    }
  }
}

.letter-render img,
.img-max-h-250 img {
  max-height: 250px !important;
}
</style>
